import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { BRAND_LUXE_PRIMARY } from '../../../../pages/BrandDashboard/brandDashboardTokens';
import { midnight } from '../../../../pages/BrandDashboard/contestWizard/midnightStyles';

const RING_R = 44;
const RING_C = 2 * Math.PI * RING_R;

export interface BrandContestJurorProgressRingProps {
    percentage: number;
    /** Outer box width/height in px. Default 160 (compact). */
    size?: number;
    /** Show "Progress" caption under percentage. Default true when size ≥ 120. */
    showLabel?: boolean;
}

const BrandContestJurorProgressRing: React.FC<BrandContestJurorProgressRingProps> = ({
    percentage,
    size = 160,
    showLabel,
}) => {
    const clamped = Math.min(100, Math.max(0, percentage));
    const offset = RING_C * (1 - clamped / 100);
    const showProgressLabel = showLabel ?? size >= 120;
    const percentFontSize = size >= 120 ? 36 : size >= 72 ? 14 : size >= 48 ? 10 : 9;

    return (
        <Box
            sx={{
                position: 'relative',
                width: size,
                height: size,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}>
            <Box
                component="svg"
                viewBox="0 0 100 100"
                sx={{
                    width: '100%',
                    height: '100%',
                    transform: 'rotate(-90deg)',
                    display: 'block',
                }}>
                <circle
                    cx="50"
                    cy="50"
                    r={RING_R}
                    fill="transparent"
                    stroke="#242424"
                    strokeWidth={size < 56 ? 7 : 8}
                />
                <circle
                    cx="50"
                    cy="50"
                    r={RING_R}
                    fill="transparent"
                    stroke={BRAND_LUXE_PRIMARY}
                    strokeWidth={size < 56 ? 7 : 8}
                    strokeDasharray={RING_C}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    style={{ filter: 'drop-shadow(0 0 8px rgba(255, 201, 101, 0.4))' }}
                />
            </Box>
            <Stack sx={{ position: 'absolute', alignItems: 'center', pointerEvents: 'none' }}>
                <Typography
                    sx={{
                        fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: percentFontSize,
                        lineHeight: 1,
                        color: BRAND_LUXE_PRIMARY,
                    }}>
                    {Math.round(clamped)}%
                </Typography>
                {showProgressLabel ? (
                    <Typography
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: 10,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.12em',
                            color: midnight.onSurfaceVariant,
                            mt: 0.5,
                        }}>
                        Progress
                    </Typography>
                ) : null}
            </Stack>
        </Box>
    );
};

export { BrandContestJurorProgressRing };
